import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { TeamService } from '../../../services/team.service';
import { UtilsService } from '../../../services/utils.service';

declare var $: any;
declare var UIkit: any;

@Component({
  selector: 'app-team-view',
  templateUrl: './team-view.component.html',
  styleUrls: ['./team-view.component.scss'],
})
export class TeamViewComponent implements OnInit {
  selectedTeamId: string;
  team: any = {
    social: { linkedin: null, twitter: null, instagram: null, facebook: null },
    hobbies: [],
    awards: [],
    certifications: [],
    publications: [],
    featured_in: [],
    photos: [],
  };

  // Districts state
  allDistricts: any[] = []; // Array of {name: enumKey, description: Label, slug: slug}

  // Reviews state (read-only)
  reviews: any[] = [];

  // Photo upload state
  selectedFile: File = null;           // for featured photo
  selectedGalleryFiles: File[] = [];   // for gallery (multi-select)
  uploadingFeatured = false;
  uploadingGallery = false;
  galleryUploadProgress = '';          // e.g. "Uploading 2/4..."

  // Audio upload state
  selectedAudioFile: File = null;
  uploadingAudio = false;

  constructor(
    public snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private teamService: TeamService,
    private utilsService: UtilsService,
  ) {
    route.params.subscribe((params) => {
      if (params['id']) {
        this.selectedTeamId = params['id'];
        this.loadDistrictOptions();
        this.loadTeamProfile();
        this.loadReviews();
      }
    });
  }

  ngOnInit() {}

  loadTeamProfile() {
    this.teamService.getTeamProfile(this.selectedTeamId).subscribe((result: any) => {
      if (result && result.data && result.data['teamProfile']) {
        this.team = JSON.parse(JSON.stringify(result.data['teamProfile']));
        if (!this.team.social) {
          this.team.social = { linkedin: null, twitter: null, instagram: null, facebook: null };
        }
        if (!this.team.photos) { this.team.photos = []; }
        // Ensure arrays are always proper arrays (never null)
        this.team.hobbies = this.team.hobbies || [];
        this.team.awards = this.team.awards || [];
        this.team.certifications = this.team.certifications || [];
        this.team.publications = this.team.publications || [];
        this.team.featured_in = this.team.featured_in || [];
        this.team.visitedDistricts = this.team.visitedDistricts || [];
      }
    });
  }

  // ── Visited Districts ──────────────────────────────────────────

  loadDistrictOptions() {
    this.teamService.getDistrictOptions().subscribe((result: any) => {
      if (result && result.data && result.data.__type && result.data.__type.enumValues) {
        this.allDistricts = result.data.__type.enumValues.map(d => {
          return {
            name: d.name, // e.g. PARO
            description: d.description, // e.g. Paro
            slug: d.name.toLowerCase().replace(/_/g, '-') // e.g. paro
          };
        });
      }
    });
  }

  hasDistrict(enumKey: string): boolean {
    if (!this.team.visitedDistricts) return false;
    // Visited districts are stored as slugs (e.g. "paro"). We find the enumKey's corresponding slug.
    const slug = enumKey.toLowerCase().replace(/_/g, '-');
    return this.team.visitedDistricts.indexOf(slug) > -1 || this.team.visitedDistricts.indexOf(enumKey) > -1;
  }

  toggleDistrict(enumKey: string) {
    if (!this.team.visitedDistricts) {
      this.team.visitedDistricts = [];
    }
    // API wants the ENUM KEY (e.g. "PARO") back in the mutation
    // So we will manage the array natively using enum keys for the update payload
    // First, let's normalize everything currently in visitedDistricts to enumKeys so we can toggle easily
    this.team.visitedDistricts = this.team.visitedDistricts.map(val => {
      const matched = this.allDistricts.find(d => d.slug === val || d.name === val);
      return matched ? matched.name : val;
    });

    const index = this.team.visitedDistricts.indexOf(enumKey);
    if (index > -1) {
      this.team.visitedDistricts.splice(index, 1);
    } else {
      this.team.visitedDistricts.push(enumKey);
    }
  }

  // ── Dynamic list helpers ─────────────────────────────────────────

  addItem(list: any[], defaultValue: any = '') {
    list.push(defaultValue);
  }

  removeItem(list: any[], index: number) {
    list.splice(index, 1);
  }

  trackByIndex(index: number): number {
    return index;
  }

  parseArrayField(str: string): string[] {
    return str
      ? str.split(',').map((s) => s.trim()).filter((s) => s.length > 0)
      : [];
  }

  updateTeam() {
    if (this.team.yearsOfExperience !== null && this.team.yearsOfExperience !== undefined && this.team.yearsOfExperience < 0) {
      this.utilsService.openSnackBar('Years of experience cannot be negative.', '');
      return;
    }

    const teamUpdate = {
      id: this.team.id,
      team_profile: {
        name: this.team.name,
        designation: this.team.designation,
        location: this.team.location,
        description: this.team.description,
        status: this.team.status,
        yearsOfExperience: this.team.yearsOfExperience || null,
        dzongkhaName: this.team.dzongkhaName || null,
        funFact: this.team.funFact || null,
        hobbies: (this.team.hobbies || []).filter(v => v && v.trim()),
        awards: (this.team.awards || []).filter(v => v && v.trim()),
        certifications: (this.team.certifications || []).filter(v => v && v.trim()),
        publications: (this.team.publications || []).filter(v => v && v.title && v.title.trim()),
        featured_in: (this.team.featured_in || []).filter(v => v && v.title && v.title.trim()),
        visitedDistricts: (this.team.visitedDistricts || []).map(val => {
          const matched = this.allDistricts.find(d => d.slug === val || d.name === val);
          return matched ? matched.name : val;
        }),
        social: {
          linkedin: this.team.social.linkedin || null,
          twitter: this.team.social.twitter || null,
          instagram: this.team.social.instagram || null,
          facebook: this.team.social.facebook || null,
        },
        audioFileId: this.team.audioFileId || (this.team.audioFile ? this.team.audioFile.id : null),
      },
    };

    this.teamService.updateTeamProfile(teamUpdate).subscribe(
      (response: any) => {
        if (response.errors && response.errors.length > 0) {
          const errors = response.errors.map((err) => err.message);
          this.utilsService.openSnackBar(errors.join(', '), '');
        } else {
          this.utilsService.openSnackBar('Team Member Updated!', '');
        }
      },
      (error) => {
        this.utilsService.openSnackBar('Unable to update team member.', '');
        console.log('Error updating team member:', error);
      },
    );
  }

  deleteTeamMember() {
    this.teamService.removeTeamMember(this.selectedTeamId).subscribe(
      (response: any) => {
        if (response.errors && response.errors.length > 0) {
          this.utilsService.openSnackBar('Error deleting team member.', '');
        } else {
          this.utilsService.openSnackBar('Team Member Deleted.', '');
          this.router.navigate(['/teams']);
        }
      },
      (error) => {
        this.utilsService.openSnackBar('Unable to delete team member.', '');
        console.log('Error deleting team member:', error);
      },
    );
  }

  // ── Guest Reviews (read-only) ────────────────────────────────────────────

  loadReviews() {
    this.teamService.getTeamProfileReviews(this.selectedTeamId).subscribe((result: any) => {
      if (result && result.data && result.data['teamProfile'] && result.data['teamProfile'].reviews) {
        this.reviews = result.data['teamProfile'].reviews;
      } else if (result && result.data && result.data['teamProfileReviews']) {
        // fallback for older API shape
        this.reviews = result.data['teamProfileReviews'].edges
          ? result.data['teamProfileReviews'].edges.map(edge => edge.node)
          : result.data['teamProfileReviews'];
      }
    });
  }


  // ── Photo Upload ──────────────────────────────────────────────────────────

  onFileSelected(event) {
    if (event.target.files && event.target.files.length > 0) {
      this.selectedFile = event.target.files[0];
    }
  }

  onAudioSelected(event) {
    if (event.target.files && event.target.files.length > 0) {
      this.selectedAudioFile = event.target.files[0];
    }
  }

  uploadAudio() {
    if (!this.selectedAudioFile) {
      this.utilsService.openSnackBar('Please select an audio file first.', '');
      return;
    }
    this.uploadingAudio = true;

    this.teamService.issuePresignedURL(this.selectedAudioFile.type).subscribe(
      (presigned: any) => {
        const { signedRequest, fileName } = presigned;
        this.teamService.uploadToS3(signedRequest, this.selectedAudioFile).subscribe(
          () => {
            const fileInput = {
              path: fileName,
              mimetype: this.selectedAudioFile.type,
              filename: this.selectedAudioFile.name,
              teamProfileId: this.selectedTeamId,
            };
            this.teamService.createFile(fileInput).subscribe(
              (fileResult: any) => {
                const newFile = fileResult.data['createFile'];
                this.team.audioFileId = newFile.id;
                this.team.audioFile = newFile;
                this.uploadingAudio = false;
                this.selectedAudioFile = null;
                // Automatically save the profile so the user doesn't lose the audio file on refresh
                this.updateTeam();
              },
              (err) => {
                this.uploadingAudio = false;
                this.utilsService.openSnackBar('Audio uploaded to S3, but DB registration failed.', '');
              },
            );
          },
          (err) => {
            this.uploadingAudio = false;
            this.utilsService.openSnackBar('Failed to upload audio to S3.', '');
          },
        );
      },
      (err) => {
        this.uploadingAudio = false;
        this.utilsService.openSnackBar('Failed to get upload URL.', '');
      },
    );
  }

  onGalleryFilesSelected(event) {
    if (event.target.files && event.target.files.length > 0) {
      this.selectedGalleryFiles = Array.from(event.target.files);
    }
  }

  uploadFeaturedPhoto() {
    if (!this.selectedFile) {
      this.utilsService.openSnackBar('Please select a file first.', '');
      return;
    }
    this.uploadingFeatured = true;

    this.teamService.issuePresignedURL(this.selectedFile.type).subscribe(
      (presigned: any) => {
        const { signedRequest, fileName } = presigned;
        this.teamService.uploadToS3(signedRequest, this.selectedFile).subscribe(
          () => {
            // Register file in DB
            const fileInput = {
              path: fileName,
              mimetype: this.selectedFile.type,
              filename: this.selectedFile.name,
              teamProfileId: this.selectedTeamId,
            };
            this.teamService.createFile(fileInput).subscribe(
              (fileResult: any) => {
                const newFile = fileResult.data['createFile'];
                // Set as featured photo
                this.teamService.setFeaturedPhoto(this.selectedTeamId, newFile.id).subscribe(
                  () => {
                    this.uploadingFeatured = false;
                    this.utilsService.openSnackBar('Featured photo updated!', '');
                    this.selectedFile = null;
                    this.team.featuredPhoto = newFile;
                  },
                  (err) => {
                    this.uploadingFeatured = false;
                    this.utilsService.openSnackBar('Photo uploaded but could not set as featured.', '');
                  },
                );
              },
              (err) => {
                this.uploadingFeatured = false;
                this.utilsService.openSnackBar('Photo uploaded to S3, but DB registration failed.', '');
              },
            );
          },
          (err) => {
            this.uploadingFeatured = false;
            this.utilsService.openSnackBar('Failed to upload photo to S3.', '');
          },
        );
      },
      (err) => {
        this.uploadingFeatured = false;
        this.utilsService.openSnackBar('Failed to get upload URL.', '');
      },
    );
  }

  uploadGalleryPhoto() {
    if (!this.selectedGalleryFiles || this.selectedGalleryFiles.length === 0) {
      this.utilsService.openSnackBar('Please select at least one file.', '');
      return;
    }
    this.uploadingGallery = true;
    const files = [...this.selectedGalleryFiles];
    const total = files.length;
    let completed = 0;
    let failed = 0;

    const uploadOne = (file: File) => {
      this.galleryUploadProgress = `Uploading ${completed + 1}/${total}...`;

      this.teamService.issuePresignedURL(file.type).subscribe(
        (presigned: any) => {
          const { signedRequest, fileName } = presigned;
          this.teamService.uploadToS3(signedRequest, file).subscribe(
            () => {
              const fileInput = {
                path: fileName,
                mimetype: file.type,
                filename: file.name,
                teamProfileId: this.selectedTeamId,
              };
              this.teamService.createFile(fileInput).subscribe(
                (fileResult: any) => {
                  const newFile = fileResult.data['createFile'];
                  this.teamService.addToGallery(this.selectedTeamId, newFile.id).subscribe(
                    () => {
                      if (!this.team.photos) this.team.photos = [];
                      this.team.photos.push(newFile);
                      completed++;
                      checkDone();
                    },
                    (err) => { failed++; completed++; checkDone(); },
                  );
                },
                (err) => { failed++; completed++; checkDone(); },
              );
            },
            (err) => { failed++; completed++; checkDone(); },
          );
        },
        (err) => { failed++; completed++; checkDone(); },
      );
    };

    const checkDone = () => {
      if (completed === total) {
        this.uploadingGallery = false;
        this.galleryUploadProgress = '';
        this.selectedGalleryFiles = [];
        if (failed === 0) {
          this.utilsService.openSnackBar(`${total} photo${total > 1 ? 's' : ''} added to gallery!`, '');
        } else {
          this.utilsService.openSnackBar(
            `${total - failed} uploaded, ${failed} failed.`, '',
          );
        }
      }
    };

    // Kick off all uploads (they'll run concurrently via presigned URLs)
    files.forEach(file => uploadOne(file));
  }

  removeGalleryPhoto(imageId: string) {
    this.teamService.removeFromGallery(this.selectedTeamId, imageId).subscribe(
      (response: any) => {
        if (!response.errors || response.errors.length === 0) {
          this.utilsService.openSnackBar('Photo removed from gallery.', '');
          this.team.photos = this.team.photos.filter((p) => p.id !== imageId);
        } else {
          this.utilsService.openSnackBar('Error removing photo.', '');
        }
      },
      (err) => {
        this.utilsService.openSnackBar('Error removing photo.', '');
      },
    );
  }

  setAsFeaturedFromGallery(imageId: string) {
    this.teamService.setFeaturedPhoto(this.selectedTeamId, imageId).subscribe(
      (response: any) => {
        if (!response.errors || response.errors.length === 0) {
          this.utilsService.openSnackBar('Featured photo updated!', '');
          const selectedPhoto = this.team.photos.find(p => p.id === imageId);
          if (selectedPhoto) {
            this.team.featuredPhoto = selectedPhoto;
          }
        } else {
          this.utilsService.openSnackBar('Error setting featured photo.', '');
        }
      },
      (err) => {
        this.utilsService.openSnackBar('Error setting featured photo.', '');
      },
    );
  }
}



// WEBPACK FOOTER //
// /Users/ugyen/Documents/Bhutan-office/my_bhutan/mybhutan-travel/src-admin/app/components/teams/team-view/team-view.component.ts